/** Minimal history router: path patterns with :params, link interception, scroll restore. */
import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type ReactNode } from "react";

const Ctx = createContext<{ path: string; search: URLSearchParams }>({ path: "/", search: new URLSearchParams() });

export function navigate(to: string, replace = false) {
  if (to === location.pathname + location.search) return;
  history[replace ? "replaceState" : "pushState"](null, "", to);
  window.dispatchEvent(new Event("dk:navigate"));
  window.scrollTo(0, 0);
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(() => ({ path: location.pathname, search: new URLSearchParams(location.search) }));
  useEffect(() => {
    const on = () => setLoc({ path: location.pathname, search: new URLSearchParams(location.search) });
    window.addEventListener("popstate", on);
    window.addEventListener("dk:navigate", on);
    return () => { window.removeEventListener("popstate", on); window.removeEventListener("dk:navigate", on); };
  }, []);
  return <Ctx.Provider value={loc}>{children}</Ctx.Provider>;
}

export const useLocation = () => useContext(Ctx);

/** Match "/bots/:id/:tab?" against a path. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean), s = path.split("/").filter(Boolean);
  const out: Record<string, string> = {};
  for (let i = 0; i < Math.max(p.length, s.length); i++) {
    const a = p[i], b = s[i];
    if (a === undefined) return null;
    const opt = a.endsWith("?"), name = a.replace(/^:/, "").replace(/\?$/, "");
    if (a.startsWith(":")) { if (b === undefined) { if (opt) continue; return null; } out[name] = decodeURIComponent(b); }
    else if (a !== b) return null;
  }
  return out;
}

export function Link({ to, children, ...rest }: { to: string; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a href={to} {...rest} onClick={(e) => {
      rest.onClick?.(e);
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault(); navigate(to);
    }}>{children}</a>
  );
}
